import * as THREE from 'three';
import { OGRE } from '../../models/ogre.ts';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, keys, lerp, outBack, outCubic, outExpo, outQuad, phase, settle, span, type Ease, type Key } from '../curves.ts';
import { apply, timeline, type Beat, type KeyPose } from '../pose.ts';

/*
 * Ogro de sombra: idle and walk (loops), attack (a wide sweep of the club that lands at ~0.13 s, when the
 * simulation deals it), hit, stun (loop), die (ends on the ground; the view then sinks it) and its roar, which
 * frightens. Everything is heavy: anticipation before each move, the club and the chain trailing behind, every
 * footfall and blow settled into the ground with a spring.
 *
 * Conventions as in human.ts: limbs hang along -Y, so a negative X swings an arm or leg forward; +X on a shin
 * bends the knee back, -X on a forearm bends the elbow forward; +X on a trunk leans it forward; +X on a jaw
 * opens it; +Y turns a trunk to its right (+X); +Z on the right arm lifts it out to the side.
 */

type OB = 'root' | 'hips' | 'chest' | 'head' | 'jaw' | 'loin' | 'armL' | 'foreL' | 'chain' | 'armR' | 'foreR' | 'club' | 'legL' | 'shinL' | 'legR' | 'shinR';
type OP = KeyPose<OB>;

/** A damped impulse at `t0` (0 before it). */
const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** Whole-figure squash from impulses: a landing flattens it a hair and it springs back. */
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** A deterministic shiver (two sines at integer multiples of `f`). */
const shiver = (t: number, f: number): number => 0.6 * Math.sin(TAU * f * t) + 0.4 * Math.sin(TAU * f * 2 * t + 1.3);

/** Held looks: a sine that lingers at its ends (a stare, then a slow turn). */
const held = (x: number, k = 0.45): number => {
  const s = Math.sin(x);
  return Math.sign(s) * Math.abs(s) ** k;
};

/** Adds the stance to every beat (so a timeline's beats only spell what changes from it). */
const over = (st: OP, beats: readonly (readonly [number, OP, Ease?])[]): Beat<OB>[] =>
  beats.map(([t, p, e]) => (e ? [t, { ...st, ...p }, e] : [t, { ...st, ...p }]) as Beat<OB>);


const _q = new THREE.Quaternion();
const _c = new THREE.Quaternion();
const _e = new THREE.Euler();
const _d = new THREE.Vector3();
const CHAIN: readonly OB[] = ['root', 'hips', 'chest', 'armR', 'foreR'];

/**
 * Turns the club (by `w`, blending with what the clip already set) so it points along the model-space
 * direction at yaw `yaw` (0 straight ahead, + toward its right) and pitch `pitch` (+ up), whatever the arm and
 * trunk are doing: for a sweep that stays flat while the body turns behind it.
 */
const aimClub = (b: Bones<OB>, yaw: number, pitch: number, w = 1): void => {
  if (w <= 0) return;
  _c.identity();
  for (const n of CHAIN) _c.multiply(_q.setFromEuler(_e.set(b[n].rx, b[n].ry, b[n].rz, 'XYZ')));
  _d.set(Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), Math.cos(yaw) * Math.cos(pitch));
  _q.setFromUnitVectors(OGRE.dir, _d);
  _c.invert().multiply(_q);
  _e.setFromQuaternion(_c, 'XYZ');
  const cb = b.club;
  cb.rx = lerp(cb.rx, _e.x, w);
  cb.ry = lerp(cb.ry, _e.y, w);
  cb.rz = lerp(cb.rz, _e.z, w);
};

/** Planted and crouched, the weight forward on the knuckles' side, the head lifted to glare from under the hump. */
const O_ST: OP = {
  root: [0, 0, 0, 0, -0.03, 0],
  hips: [0.05, 0, 0],
  chest: [0.06, 0, 0],
  head: [-0.12, 0, 0],
  armL: [-0.08, 0, -0.04],
  foreL: [-0.12, 0, 0],
  armR: [0, 0, 0.04],
  legL: [-0.12, 0, -0.03],
  shinL: [0.2, 0, 0],
  legR: [-0.1, 0, 0.03],
  shinR: [0.18, 0, 0],
};

/** The hanging bits trail the body: the chain from the wrist, the flap from the belt. */
const LAG: Partial<Record<OB, number>> = { head: 0.03, jaw: 0.03, chain: 0.07, loin: 0.06, club: 0.03, foreL: 0.02, foreR: 0.02 };

/** Heavy breathing: the chest heaves and the shoulders rise on each breath, the jaw sags open on the way out. */
const ogreIdle = (): Clip<OB> =>
  clip<OB>('idle', 3.6, true, (b, t) => {
    const p = phase(t, 3.6) * TAU;
    apply(b, O_ST);
    const br = Math.sin(p * 2);
    const out = Math.max(0, -Math.sin(p * 2 - 0.4)) ** 2;
    b.root.pos(0.008 * Math.sin(p), -0.012 * (1 + br), 0);
    b.chest.rot(-0.045 * br, 0.05 * Math.sin(p), 0.02 * Math.sin(p + 0.4)).scale(1 + 0.012 * br);
    b.hips.rot(0.015 * br, -0.02 * Math.sin(p), 0);
    b.head.rot(0.05 * br - 0.06 * Math.max(0, Math.sin(p + 0.6)), 0.3 * held(p + 2), 0.04 * Math.sin(p + 1));
    b.jaw.rot(0.18 * out);
    b.armL.rot(0.04 * br, 0, -0.04 * br);
    b.armR.rot(0.03 * br, 0, 0.035 * br);
    // The left fist clenches twice a loop; the club rolls a little in the right.
    b.foreL.rot(-0.1 * Math.max(0, Math.sin(p * 2 + 1)) ** 4);
    b.club.rot(0.03 * Math.sin(p), 0.05 * Math.sin(p + 0.5), 0);
    b.chain.rot(0.05 * Math.sin(p * 2 - 0.8), 0, 0.06 * Math.sin(p - 1));
    b.loin.rot(0.03 * Math.sin(p * 2 - 1), 0, 0.02 * Math.sin(p - 0.7));
    b.legL.rot(0, 0, -0.015 * Math.sin(p));
    b.legR.rot(0, 0, -0.015 * Math.sin(p));
  }, (t) => {
    const br = Math.sin(phase(t, 3.6) * TAU * 2);
    return [1 + 0.012 * br, 1 - 0.01 * br, 1 + 0.012 * br];
  });

/**
 * Lumbering: it rolls its whole weight onto each foot (the body sways over the planted leg and drops on every
 * footfall), the free arm swings, and the club scrapes along behind, bouncing a little on each landing.
 */
const ogreWalk = (dur = 1.5): Clip<OB> =>
  clip<OB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const c2 = Math.cos(2 * th);
    const land = Math.max(0, -Math.cos(2 * th - 0.35)) ** 4;
    apply(b, O_ST, 0.85);
    const A = 0.44;
    b.legL.rot(-A * sn - 0.06, 0, -0.03);
    b.legR.rot(A * sn - 0.06, 0, 0.03);
    b.shinL.rot(1.0 * Math.max(0, cs) ** 1.5 + 0.35 * Math.max(0, -sn) + 0.12 + 0.12 * land);
    b.shinR.rot(1.0 * Math.max(0, -cs) ** 1.5 + 0.35 * Math.max(0, sn) + 0.12 + 0.12 * land);
    // The weight rolls from foot to foot: side sway, a tilt over the planted leg, and a drop on landing.
    b.root.pos(0.045 * Math.sin(th - 0.5), 0.03 * c2 - 0.01 - 0.03 * land, 0).rot(0, 0.06 * sn, -0.06 * Math.sin(th - 0.5));
    b.hips.rot(0.04 * land, 0.12 * sn, 0.03 * Math.sin(th - 0.5));
    b.chest.rot(0.06 + 0.04 * Math.cos(2 * th - 0.6) + 0.04 * land, -0.17 * sn, 0.05 * Math.sin(th - 0.9));
    b.head.rot(-0.04 * Math.cos(2 * th - 1.0) - 0.05 * land, 0.12 * sn, -0.05 * Math.sin(th - 1.1));
    b.jaw.rot(0.06 * land);
    // The free arm swings against its leg; the club arm hangs and drags.
    b.armL.rot(0.36 * Math.sin(th - 0.35), 0, -0.05 - 0.04 * land);
    b.foreL.rot(-0.25 * Math.max(0, -Math.sin(th - 0.7)));
    b.armR.rot(-0.08 * Math.sin(th - 0.5) + 0.06, 0, 0.06 + 0.03 * Math.cos(2 * th - 0.8));
    b.foreR.rot(-0.05 * Math.cos(2 * th - 1));
    b.club.rot(-0.05 * land + 0.03 * Math.cos(2 * th - 1.2), 0.07 * Math.sin(th - 1.2), 0.08 * land);
    b.chain.rot(0.25 * Math.sin(th - 1.4) + 0.1, 0, 0.1 * Math.sin(th - 1.6));
    b.loin.rot(0.2 * Math.sin(th - 1.2) + 0.05, 0, 0.06 * Math.sin(th - 1.5));
  }, (t) => {
    const k = Math.max(0, -Math.cos(phase(t, 1.5) * TAU * 2 - 0.35)) ** 4;
    return [1 + 0.03 * k, 1 - 0.035 * k, 1 + 0.03 * k];
  });

/**
 * The sweep: it heaves the club up and back past its right flank in one short, hard coil and brings it round flat across its front, the whole trunk turning behind the blow (it lands at 0.13 s); it
 * carries through to its left, steps into it, holds a beat and hauls the club back down to drag.
 */
const O_COIL: OP = {
  root: [0, 0.12, 0, 0, -0.07, -0.04],
  hips: [0.05, 0.36, 0],
  chest: [0.0, 0.82, -0.06],
  head: [-0.2, -0.55, 0],
  armL: [-0.5, 0, -0.35],
  foreL: [-0.6, 0, 0],
  armR: [0.35, 0, 1.05],
  foreR: [-0.35, 0, 0],
  legL: [-0.25, 0, -0.06],
  shinL: [0.3, 0, 0],
  legR: [0.05, 0, 0.06],
  shinR: [0.3, 0, 0],
};
const O_SWEEP: OP = {
  root: [0, -0.15, 0, 0, -0.06, 0.06],
  hips: [0.1, -0.25, 0],
  chest: [0.12, -0.55, 0.05],
  head: [-0.1, 0.25, 0],
  jaw: [0.45, 0, 0],
  armL: [0.35, 0, -0.45],
  foreL: [-0.4, 0, 0],
  armR: [-1.2, 0, 0.55],
  foreR: [-0.35, 0, 0],
  legL: [-0.45, 0, -0.06],
  shinL: [0.4, 0, 0],
  legR: [0.12, 0, 0.06],
  shinR: [0.42, 0, 0],
};
const O_THROUGH: OP = {
  ...O_SWEEP,
  root: [0, -0.3, 0, 0, -0.06, 0.08],
  hips: [0.12, -0.4, 0],
  chest: [0.16, -0.85, 0.08],
  head: [-0.05, 0.4, 0],
  jaw: [0.3, 0, 0],
  // The elbow folds as the club wraps round to its left, the fist pulled in across the chest.
  armR: [-0.85, 0, -0.1],
  foreR: [-0.95, 0, 0],
};

/**
 * The club's yaw and pitch over the sweep: back past its right flank, round through the front (0.13 s), across to
 * its left; then it drops and is dragged back across the ground to its right side.
 */
const SWEEP_YAW: Key[] = [[0, 2.44], [0.07, 2.35, outQuad], [0.13, 0.1, inCubic], [0.28, -1.35, outCubic], [0.38, -1.45], [0.95, 2.44, inOut]];
const SWEEP_PITCH: Key[] = [[0, -0.25], [0.07, 0.05, outQuad], [0.13, -0.12, inCubic], [0.28, -0.05, outCubic], [0.38, -0.1], [0.55, -0.22, inQuad], [0.95, -0.25]];

const ogreAttack = (): Clip<OB> =>
  clip<OB>('attack', 0.95, false, (b, t) => {
    timeline(b, t, over(O_ST, [
      [0, {}],
      [0.07, O_COIL, outQuad],
      [0.13, O_SWEEP, inCubic],
      [0.28, O_THROUGH, outCubic],
      [0.38, { ...O_THROUGH, chest: [0.18, -0.82, 0.07], armR: [-0.8, 0, -0.08], foreR: [-1.0, 0, 0] }],
      // It hauls the club back round and lets it drop to drag.
      [0.95, {}, inOut],
    ]), LAG);
    // The shock of the blow up the arm and through the trunk.
    const k = kick(t, 0.13, 5, 10);
    b.chest.rot(0.03 * k);
    b.armR.rot(0.08 * k);
    // The club flies flat round the body, then sags back to the ground.
    aimClub(b, keys(t, SWEEP_YAW) - 0.06 * k, keys(t, SWEEP_PITCH), span(t, 0, 0.04) * (1 - span(t, 0.82, 0.95)));
  }, (t) => thud(t, [[0.13, 0.03], [0.28, 0.02]]));

/** It barely flinches: the trunk rocks back, the head snaps, the jaw drops; the chain jangles. */
const ogreHit = (): Clip<OB> =>
  clip<OB>('hit', 0.4, false, (b, t) => {
    apply(b, O_ST);
    const k = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    b.chest.rot(-0.2 * k, 0.1 * k, 0.05 * k);
    b.hips.rot(-0.06 * k);
    b.head.rot(-0.38 * k, -0.18 * k);
    b.jaw.rot(0.3 * a);
    b.armL.rot(-0.12 * k, 0, -0.1 * a);
    b.armR.rot(-0.06 * k, 0, 0.08 * a);
    b.root.pos(0, -0.015 * a, -0.04 * k);
    b.shinL.rot(0.1 * a);
    b.shinR.rot(0.12 * a);
    const lag = settle(t - 0.05, 2.2, 6) * span(t, 0.02, 0.07);
    b.chain.rot(0.4 * lag, 0, 0.2 * lag);
    b.loin.rot(0.2 * lag);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.04 * k, 1 - 0.05 * k, 1 + 0.04 * k];
  });

/** Dazed: it reels on its feet, the head lolling, the free arm hanging slack, the club forgotten on the ground. */
const ogreStun = (): Clip<OB> =>
  clip<OB>('stun', 2, true, (b, t) => {
    const p = phase(t, 2) * TAU;
    apply(b, O_ST, 0.7);
    b.root.rot(0.035 * Math.sin(p), 0, 0.07 * Math.cos(p)).pos(0.03 * Math.cos(p), -0.02, 0);
    b.hips.rot(0.06, 0, -0.03 * Math.cos(p));
    b.chest.rot(0.25 + 0.06 * Math.sin(p * 2), 0.08 * Math.sin(p), -0.08 * Math.cos(p));
    b.head.rot(0.3 + 0.18 * Math.sin(p + 0.8), 0.35 * Math.cos(p + 0.8), 0.25 * Math.sin(p + 0.8));
    b.jaw.rot(0.35 + 0.08 * Math.sin(p * 2));
    b.armL.rot(0.15 * Math.sin(p + 1.4), 0, 0.03 + 0.06 * Math.cos(p + 1.4));
    b.foreL.rot(0.1);
    b.armR.rot(-0.1 * Math.sin(p + 1.2), 0, 0.05 * Math.cos(p + 1.4));
    b.chain.rot(0.2 * Math.sin(p + 2.2), 0, 0.15 * Math.cos(p + 2.2));
    b.loin.rot(0.06 * Math.sin(p + 2), 0, 0.06 * Math.cos(p + 2));
    b.shinL.rot(0.1);
    b.shinR.rot(0.1 + 0.08 * Math.sin(p));
  });

/**
 * Struck down: it rocks back with a bellow, sags, the knees give and it drops onto them with a thud that shakes
 * it, sways there a beat with the club slipping from its fist, and then pitches forward onto its face.
 */
const ogreDie = (): Clip<OB> =>
  clip<OB>('die', 2.4, false, (b, t) => {
    const knees: OP = { legL: [0.05, 0, -0.12], shinL: [1.55, 0, 0], legR: [0.1, 0, 0.12], shinR: [1.6, 0, 0] };
    const kneel: OP = { ...knees, root: [0, 0, 0, 0, -0.36, -0.05], hips: [0.15, 0, 0], chest: [0.25, 0.1, 0.05], head: [0.35, 0.15, 0.1], jaw: [0.4, 0, 0], armL: [-0.3, 0, -0.25], foreL: [-0.55, 0, 0], armR: [-0.2, 0, 0.3], foreR: [-0.35, 0, 0], club: [0.1, 0, 0.15] };
    const flat: OP = { legL: [0.3, 0, -0.15], shinL: [0.35, 0, 0], legR: [0.25, 0, 0.15], shinR: [0.4, 0, 0], root: [1.3, 0, 0.05, 0, 0.12, -0.45], hips: [-0.3, 0, 0], chest: [-0.3, 0.1, 0.1], head: [-0.5, 0.7, 0.2], jaw: [0.5, 0, 0], armL: [-2.4, 0, -0.5], foreL: [-0.3, 0, 0], armR: [-1.1, 0, 0.9], foreR: [-0.2, 0, 0], club: [0.3, 0, 0.4], loin: [-1.4, 0, 0], chain: [-1.2, 0, 0] };
    timeline(b, t, [
      [0, O_ST],
      [0.22, { ...O_ST, root: [0, 0, 0, 0, 0.0, -0.06], chest: [-0.4, 0.1, 0.08], hips: [-0.1, 0, 0], head: [-0.65, 0.15, 0], jaw: [0.85, 0, 0], armL: [-0.4, 0, -0.75], foreL: [-0.5, 0, 0], armR: [-0.2, 0, 0.6], foreR: [-0.3, 0, 0], legL: [0.08, 0, -0.03], legR: [-0.15, 0, 0.03] }, outExpo],
      [0.55, { ...O_ST, root: [0, 0, 0, 0, -0.08, -0.03], chest: [0.15, 0.05, 0.05], head: [0.2, 0.1, 0], jaw: [0.4, 0, 0], armL: [0.05, 0, -0.15], armR: [0.05, 0, 0.1], legL: [-0.25, 0, -0.06], shinL: [0.45, 0, 0], legR: [-0.25, 0, 0.06], shinR: [0.45, 0, 0] }, inOut],
      [0.82, kneel, inQuad],
      [0.95, { ...kneel, root: [0, 0, 0, 0, -0.34, -0.05], chest: [0.32, 0.1, 0.05], head: [0.45, 0.15, 0.1] }, outQuad],
      [1.35, { ...kneel, chest: [0.15, 0.1, 0.1], head: [0.15, 0.25, 0.15], club: [0.3, 0, 0.3] }, inOut],
      [1.6, { ...kneel, root: [0.1, 0, 0, 0, -0.35, -0.03], hips: [0.25, 0, 0], chest: [0.3, 0.1, 0.1], head: [0.3, 0.2, 0.1], armL: [-0.3, 0, -0.1], armR: [-0.2, 0, 0.2], club: [0.35, 0, 0.3] }, inOut],
      [2.05, flat, inQuad],
      [2.4, { ...flat, root: [1.32, 0, 0.05, 0, 0.11, -0.45], head: [-0.52, 0.72, 0.22] }, outBack],
    ], LAG);
    // The club slips from its fist and falls flat beside it.
    aimClub(b, keys(t, [[1.2, 0.9], [2.05, 1.25, inQuad]]), keys(t, [[1.2, -0.2], [1.6, -0.12], [2.05, -0.02, inQuad]]), span(t, 1.1, 1.5, inQuad));
  }, (t) => thud(t, [[0.82, 0.05], [2.05, 0.05]]));

/** The roar: it sinks into a crouch, then throws its chest out, arms flung wide and head back, and bellows, shaking. */
const ogreRoar = (): Clip<OB> =>
  clip<OB>('roar', 1.2, false, (b, t) => {
    const peak: OP = {
      root: [0, 0, 0, 0, 0.02, -0.02], hips: [-0.12, 0, 0], chest: [-0.45, 0, 0], head: [-0.7, 0, 0], jaw: [0.95, 0, 0],
      armL: [0.3, 0, -1.25], foreL: [-0.55, 0, 0], armR: [0.3, 0, 1.15], foreR: [-0.45, 0, 0], club: [0, 0, -0.3],
      legL: [-0.05, 0, -0.08], shinL: [0.1, 0, 0], legR: [-0.05, 0, 0.08], shinR: [0.1, 0, 0],
    };
    timeline(b, t, over(O_ST, [
      [0, {}],
      [0.28, { root: [0, 0, 0, 0, -0.09, 0.02], hips: [0.15, 0, 0], chest: [0.35, 0, 0], head: [0.3, 0, 0], armL: [-0.15, 0, 0.12], foreL: [-0.5, 0, 0], armR: [-0.1, 0, -0.05], foreR: [-0.4, 0, 0], legL: [-0.3, 0, -0.05], shinL: [0.5, 0, 0], legR: [-0.28, 0, 0.05], shinR: [0.5, 0, 0] }, inOut],
      [0.42, peak, outBack],
      [0.92, { ...peak, chest: [-0.42, 0, 0], head: [-0.66, 0, 0], armL: [0.28, 0, -1.2], armR: [0.28, 0, 1.1] }],
      [1.2, {}, inOut],
    ]), LAG);
    // The club brandished up and out at the end of the arm.
    aimClub(b, 1.45, 0.55, envelope(t, 0.22, 0.42, 0.92, 1.15));
    const tr = envelope(t, 0.4, 0.48, 0.88, 1.0) * shiver(t, 10);
    b.chest.rot(0.02 * tr, 0.02 * tr, 0.02 * tr);
    b.head.rot(0.04 * tr, 0.04 * tr);
    b.jaw.rot(0.06 * tr);
    b.foreL.rot(0.06 * tr);
    b.foreR.rot(0.06 * tr);
  }, (t) => thud(t, [[0.42, -0.03]]), 'Rugido');

export const ogreClips = (): Clip[] => [ogreIdle(), ogreWalk(), ogreAttack(), ogreHit(), ogreStun(), ogreDie(), ogreRoar()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { ogre: ogreClips };
